import { Injectable, inject } from '@angular/core';

import { Store } from '@ngxs/store';

import { jwtDecode } from 'jwt-decode';

import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../../../environments/environment';

import { CognitoState } from '../state/store/cognito.state';

/**
 * UX-only profile helper (piano Fase 6, task 9): reads the `cognito:groups` claim already
 * carried by the ID token this app sends as Bearer (see `CognitoTokenInterceptor`) to decide
 * whether the current user is Multimedia, purely to hide write controls/Multimedia-only
 * navigation. This is never the enforcement boundary — the backend's
 * `AuthorizationScopeResolver` (Fase 3) is, and rejects unauthorized writes independently of
 * anything this service returns.
 */
@Injectable({ providedIn: 'root' })
export class AuthProfileService {
  public static readonly MULTIMEDIA_GROUP = 'Multimedia';
  private static readonly GROUPS_CLAIM = 'cognito:groups';
  private static readonly ID_ORGANIZATION_CLAIM = 'custom:id_organization';

  private readonly store = inject(Store);

  // ponytail: noAuth (piano ui-fixes, F0) has no real cognito token to decode, so the
  // group-derived flag is always false there and every CRUD control stays hidden in
  // devnoauth. Same UX-only reasoning as emed-main-dispatcher.component.ts: force `true`
  // when environment.noAuth, the backend enforcement is untouched.
  public readonly isMultimedia$: Observable<boolean> = environment.noAuth
    ? of(true)
    : this.store.select(CognitoState.getIdToken).pipe(map((idToken) => this.hasGroup(idToken, AuthProfileService.MULTIMEDIA_GROUP)));

  /**
   * The exact organization the caller's token is anchored to (`custom:id_organization`),
   * mirroring `AuthorizationScopeResolver#resolveOwnOrganizationId` — `null` for Multimedia
   * (no single anchor) or when the claim is absent/unreadable. UX only: used to default/lock
   * the organization field on self-service forms (contacts). The backend re-derives and
   * enforces the same value independently.
   */
  public readonly ownOrganizationId$: Observable<number | null> = this.store
    .select(CognitoState.getIdToken)
    .pipe(map((idToken) => this.ownOrganizationIdOf(idToken)));

  /** Synchronous snapshot, for guards and one-shot checks outside a template binding. */
  public isMultimediaSnapshot(): boolean {
    if (environment.noAuth) {
      return true;
    }
    const idToken = this.store.selectSnapshot(CognitoState.getIdToken);
    return this.hasGroup(idToken, AuthProfileService.MULTIMEDIA_GROUP);
  }

  public ownOrganizationIdSnapshot(): number | null {
    const idToken = this.store.selectSnapshot(CognitoState.getIdToken);
    return this.ownOrganizationIdOf(idToken);
  }

  private ownOrganizationIdOf(idToken: string): number | null {
    if (!idToken || this.hasGroup(idToken, AuthProfileService.MULTIMEDIA_GROUP)) {
      return null;
    }
    try {
      const decoded: Record<string, unknown> = jwtDecode(idToken);
      const raw = decoded[AuthProfileService.ID_ORGANIZATION_CLAIM];
      const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      return Number.isNaN(parsed) ? null : parsed;
    } catch {
      return null;
    }
  }

  private hasGroup(idToken: string, group: string): boolean {
    if (!idToken) {
      return false;
    }
    try {
      const decoded: Record<string, unknown> = jwtDecode(idToken);
      const groups = decoded[AuthProfileService.GROUPS_CLAIM];
      return Array.isArray(groups) && groups.includes(group);
    } catch {
      return false;
    }
  }
}
